import React, { Fragment, useState, useEffect, useMemo } from "react";
import { Grid, Segment, Select } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import PageTitle from "../../../components/page-title/PageTitle";
import PendingRegistrations from "./PendingRegistrations";
import { Grade, Student } from "../types";
import agent from "../../../app/api/agent";
import Loader from "../../../components/Loader";
import ListFilter from "../../../components/list-filter/ListFilter";
import {
  ListFilterConfig,
  ListFilterConfigItemMultiSelectOption,
  ListFilterConfigItemType,
} from "../../../components/list-filter/models";
import StudentList from "./StudentList";
import Pagination from "../../../components/pagination/Pagination";

const years = Array.from({ length: new Date().getFullYear() - 2021 + 1 }, (_, i) => 2021 + i);

const StudentDashboard = () => {
  const { t } = useTranslation();
  const [grades, setGrades] = useState<Grade[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [pendingRegistrations, setPendingRegistrations] = useState<Student[]>([]);
  const [pendingSort, setPendingSort] = useState<{ column: string; direction: "asc" | "desc" } | null>(null);
  const [isLoading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ pageNumber: 1, totalPages: 0 });
  const [pageSize, setPageSize] = useState(30);

  const filterConfig: ListFilterConfig = useMemo(
    () => [
      {
        name: "grade",
        title: "students_filter.grade.title",
        type: ListFilterConfigItemType.MultiSelect,
        options: grades.map((g) => ({ key: g.grade, value: g.grade, text: g.grade })),
      },
      {
        name: "registrationYear",
        title: "students_filter.registrationYear",
        type: ListFilterConfigItemType.MultiSelect,
        options: years.map((year) => ({ key: year, value: year, text: String(year) })),
      },
      {
        name: "applicationYear",
        title: "students_filter.applicationYear",
        type: ListFilterConfigItemType.MultiSelect,
        options: years.map((year) => ({ key: year, value: year, text: String(year) })),
      },
      {
        name: "onlyDeactivated",
        title: "students_filter.only_deactivated.title",
        type: ListFilterConfigItemType.MultiSelect,
        options: [{ key: "true", value: "true", text: t("students_filter.only_deactivated.yes") }],
      },
      {
        name: "agreement",
        title: "students_filter.agreement.title",
        type: ListFilterConfigItemType.MultiSelect,
        options: [
          { key: "none", value: "none", text: t("students_filter.agreement.not_uploaded") },
          { key: "uploaded", value: "uploaded", text: t("students_filter.agreement.uploaded") },
          { key: "approved", value: "approved", text: t("students_filter.agreement.approved") },
        ],
      },
      {
        name: "name",
        title: "students_filter.fullName",
        type: ListFilterConfigItemType.Search,
      },
    ],
    [grades, t]
  );

  const [filter, setFilter] = useState<{
    grade: ListFilterConfigItemMultiSelectOption[];
    onlyDeactivated: ListFilterConfigItemMultiSelectOption[];
    registrationYear: ListFilterConfigItemMultiSelectOption[];
    applicationYear: ListFilterConfigItemMultiSelectOption[];
    agreement: ListFilterConfigItemMultiSelectOption[];
    name: string;
  }>({ grade: [], onlyDeactivated: [], name: "", registrationYear: [], applicationYear: [], agreement: [] });

  useEffect(() => {
    loadGrades();
    loadPendingRegistrations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadPendingRegistrations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSort]);

  useEffect(() => {
    loadStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, pagination.pageNumber, pageSize]);

  const loadGrades = () => {
    agent.Schools.listGrades().then((response) => setGrades(response));
  };

  const loadPendingRegistrations = () => {
    agent.Students.listPendingRegistrations(pendingSort?.column, pendingSort?.direction).then((response) => {
      setPendingRegistrations(response);
    });
  };

  const handlePendingSortChange = (column: string) => {
    setPendingSort((prev) => {
      if (prev?.column === column) {
        if (prev.direction === "asc") return { column, direction: "desc" };
        return null;
      }
      return { column, direction: "asc" };
    });
  };

  const loadStudents = () => {
    setLoading(true);

    const params = {
      grade: filter.grade.map((v) => String(v.value)),
      onlyDeactivated: filter.onlyDeactivated.map((v) => String(v.value)),
      name: filter.name.trim().length > 0 ? filter.name : undefined,
      registrationYear: filter.registrationYear.map((v) => Number(v.value)),
      applicationYear: filter.applicationYear.map((v) => Number(v.value)),
      agreement: filter.agreement.map((v) => String(v.value)),
      activePage: pagination.pageNumber,
      pageSize,
    };

    agent.Students.list(params)
      .then((response) => {
        setStudents(response.items);
        setPagination({ pageNumber: response.pageNumber, totalPages: response.totalPages });
      })
      .finally(() => setLoading(false));
  };

  const handleApprove = (studentId: number) => {
    agent.Students.approveRegistration(studentId).then(() => {
      loadGrades();
      loadPendingRegistrations();
      loadStudents();
    });
  };

  const handleReject = (studentId: number) => {
    agent.Students.rejectRegistration(studentId).then(() => {
      loadPendingRegistrations();
    });
  };

  const handleDeleteRegistration = (studentId: number) => {
    agent.Students.deleteStudent(studentId).then(() => {
      loadPendingRegistrations();
    });
  };

  const handlePageChange = (pageNumber: number) => {
    setPagination({ ...pagination, pageNumber });
  };

  const handlePageSizeChange = (value: number) => {
    setPageSize(value);
    setPagination({ ...pagination, pageNumber: 1 });
  };

  const handleFilterChange = (value: {
    grade: ListFilterConfigItemMultiSelectOption[];
    onlyDeactivated: ListFilterConfigItemMultiSelectOption[];
    registrationYear: ListFilterConfigItemMultiSelectOption[];
    applicationYear: ListFilterConfigItemMultiSelectOption[];
    agreement: ListFilterConfigItemMultiSelectOption[];
    name: string;
  }) => {
    setFilter(value);
    setPagination({ ...pagination, pageNumber: 1 });
  };

  const renderApprovedStudents = () => {
    if (isLoading) return <Loader />;

    return students.length > 0 ? (
      <>
        <Segment>
          <StudentList students={students} onStudentUpdate={loadStudents} />
        </Segment>
        <Pagination
          totalPages={pagination.totalPages}
          pageNumber={pagination.pageNumber}
          handlePageChange={handlePageChange}
        />
      </>
    ) : (
      t("common.no_records")
    );
  };

  return (
    <Fragment>
      <PageTitle title={t("common.students")} />
      {pendingRegistrations.length > 0 && (
        <PendingRegistrations
          pendingRegistrations={pendingRegistrations}
          handleApprove={handleApprove}
          handleReject={handleReject}
          handleDelete={handleDeleteRegistration}
          sort={pendingSort}
          onSortChange={handlePendingSortChange}
        />
      )}
      <Grid>
        <ListFilter config={filterConfig} value={filter} onChange={(v) => handleFilterChange(v as any)} />
      </Grid>
      {renderApprovedStudents()}
      <div className="pagination-row">
        <Select
          compact
          upward
          options={[
            { key: 30, value: 30, text: "30" },
            { key: 50, value: 50, text: "50" },
            { key: 100, value: 100, text: "100" },
          ]}
          value={pageSize}
          onChange={(_, data) => handlePageSizeChange(data.value as number)}
        />
      </div>
    </Fragment>
  );
};

export default StudentDashboard;
